import React from 'react'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import { Card, Descriptions } from 'antd'

import {
  formatBaht,
  formatDateTime,
  formatDays,
  formatNumber,
  formatPercent,
  summarizeSeasonality
} from './format'

const sectionStyle = { marginBottom: 16 }

const Detail = ({ performance, i18n }) => {
  const t = key => i18n.t(`customerForm.${key}`)
  const descProps = { column: { xs: 1, sm: 2, md: 3 }, bordered: true, size: 'small' }

  return (
    <div>
      <Card title={t('clvHistory')} style={sectionStyle}>
        <Descriptions {...descProps}>
          <Descriptions.Item label={t('avgDaysBetweenOrders')}>
            {performance.avgDaysBetweenOrders == null ? '-' : formatDays(performance.avgDaysBetweenOrders)}
          </Descriptions.Item>
          <Descriptions.Item label={t('averageOrderYield')}>
            {formatBaht(performance.averageOrderYieldSatangs)}
          </Descriptions.Item>
          <Descriptions.Item label={t('lifetimeMarginPercent')}>
            {formatPercent(performance.lifetimeMarginPercent)}
          </Descriptions.Item>
          <Descriptions.Item label={t('predictedClvCalculatedAt')}>
            {formatDateTime(performance.predictedClvCalculatedAt)}
          </Descriptions.Item>
          <Descriptions.Item label={t('clvSegmentChangedAt')}>
            {formatDateTime(performance.clvSegmentChangedAt)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t('clvOrderActivity')} style={sectionStyle}>
        <Descriptions {...descProps}>
          <Descriptions.Item label={t('totalOrderCount')}>
            {formatNumber(performance.totalOrderCount)}
          </Descriptions.Item>
          <Descriptions.Item label={t('completeOrderCount')}>
            {formatNumber(performance.completeOrderCount)}
          </Descriptions.Item>
          <Descriptions.Item label={t('cancelledOrderCount')}>
            {formatNumber(performance.cancelledOrderCount)}
          </Descriptions.Item>
          <Descriptions.Item label={t('conversionRate')}>
            {formatPercent(performance.conversionRate)}
          </Descriptions.Item>
          <Descriptions.Item label={t('cancellationRatePercent')}>
            {formatPercent(performance.cancellationRatePercent)}
          </Descriptions.Item>
          <Descriptions.Item label={t('completeOrderAvgLeadTimeDays')}>
            {performance.completeOrderAvgLeadTimeDays == null
              ? '-'
              : `${Number(performance.completeOrderAvgLeadTimeDays).toFixed(1)} days`}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t('clvRefunds')} style={sectionStyle}>
        <Descriptions {...descProps}>
          <Descriptions.Item label={t('refundedOrderCount')}>
            {formatNumber(performance.refundedOrderCount)}
          </Descriptions.Item>
          <Descriptions.Item label={t('refundedRevenue')}>
            {formatBaht(performance.refundedRevenueSatangs)}
          </Descriptions.Item>
          <Descriptions.Item label={t('refundedYield')}>
            {formatBaht(performance.refundedYieldSatangs)}
          </Descriptions.Item>
          <Descriptions.Item label={t('refundRatePercent')}>
            {formatPercent(performance.refundRatePercent)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t('clvCostToServe')} style={sectionStyle}>
        <Descriptions {...descProps}>
          <Descriptions.Item label={t('lifetimeLogisticsCost')}>
            {formatBaht(performance.lifetimeLogisticsCostSatangs)}
          </Descriptions.Item>
          <Descriptions.Item label={t('voucherRedeemedCount')}>
            {formatNumber(performance.voucherRedeemedCount)}
          </Descriptions.Item>
          <Descriptions.Item label={t('voucherRevenueSharePercent')}>
            {formatPercent(performance.voucherRevenueSharePercent)}
          </Descriptions.Item>
          <Descriptions.Item label={t('lifetimeDiscount')}>
            {formatBaht(performance.lifetimeDiscountSatangs)}
          </Descriptions.Item>
          <Descriptions.Item label={t('staffTouchCount')}>
            {formatNumber(performance.staffTouchCount)}
          </Descriptions.Item>
          <Descriptions.Item label={t('staffTouchLastAt')}>
            {formatDateTime(performance.staffTouchLastAt)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t('clvEcommerceSignals')} style={sectionStyle}>
        <Descriptions {...descProps}>
          <Descriptions.Item label={t('cartAbandonmentCount')}>
            {formatNumber(performance.cartAbandonmentCount)}
          </Descriptions.Item>
          <Descriptions.Item label={t('lastCartAbandonedAt')}>
            {formatDateTime(performance.lastCartAbandonedAt)}
          </Descriptions.Item>
          <Descriptions.Item label={t('walletBalance')}>
            {formatBaht(performance.walletBalanceSatangs)}
          </Descriptions.Item>
          <Descriptions.Item label={t('distinctCategoryCount')}>
            {formatNumber(performance.distinctCategoryCount)}
          </Descriptions.Item>
          <Descriptions.Item label={t('primaryCategory')}>
            {performance.primaryCategory ? performance.primaryCategory.name : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('primaryCategorySharePercent')}>
            {formatPercent(performance.primaryCategorySharePercent)}
          </Descriptions.Item>
          <Descriptions.Item label={t('seasonalityIndex')} span={3}>
            {summarizeSeasonality(performance.seasonalityIndex)}
          </Descriptions.Item>
          <Descriptions.Item label={t('heavyComputedAt')} span={3}>
            {formatDateTime(performance.heavyComputedAt)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t('clvB2b')} style={sectionStyle}>
        <Descriptions {...descProps}>
          <Descriptions.Item label={t('companyCount')}>
            {formatNumber(performance.companyCount)}
          </Descriptions.Item>
          <Descriptions.Item label={t('projectCount')}>
            {formatNumber(performance.projectCount)}
          </Descriptions.Item>
          <Descriptions.Item label={t('largestOrderValue')}>
            {formatBaht(performance.largestOrderValueSatangs)}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  )
}

Detail.propTypes = {
  performance: PropTypes.object.isRequired,
  i18n: PropTypes.object.isRequired
}

export default withTranslation('common')(Detail)
